import { useCallback, useMemo } from "react";
import { useAnalystInstances } from "./AnalystRunsApi";

export interface UseAnalystInstanceNamesReturnValue {
  /**
   * Resolves an analyst instance id (string or number, as it arrives from
   * GraphQL filter values) to its display name, falling back to
   * "Name unavailable" when the instance is unknown — failed request, or a
   * user without the AI permission.
   */
  getAnalystDisplayName: (id: unknown) => string;
  findAnalystName: (id: unknown) => string | undefined;
  isLoading: boolean;
}

const useAnalystInstanceNames = (
  enabled: boolean
): UseAnalystInstanceNamesReturnValue => {
  const { data, isLoading } = useAnalystInstances(enabled);

  const namesById = useMemo(
    () =>
      new Map(
        (data?.agent_instances ?? []).map((instance) => [
          String(instance.agent_instance_id),
          instance.display_name,
        ])
      ),
    [data]
  );

  const findAnalystName = useCallback(
    (id: unknown) => namesById.get(String(id)),
    [namesById]
  );

  // Not expected today since analyst instances can't be deleted, but an id
  // with no matching instance shows "Name unavailable" rather than the raw id.
  const getAnalystDisplayName = useCallback(
    (id: unknown) => findAnalystName(id) ?? "Name unavailable",
    [findAnalystName]
  );

  return { getAnalystDisplayName, findAnalystName, isLoading };
};

export default useAnalystInstanceNames;
